<template lang="pug">
.event-registration
  loading(v-if="!loaded")
  .closed(v-else-if="isClosed")
    h3 {{ __('Challenge Complete') }}
  .scoring-not-open(v-else-if="!isScoringOpen && loggedIn && alreadyRegistered")
    p {{ __('Score submission will open when the competition begins. Good luck!') }}
  .already-registered(v-else-if="loggedIn && alreadyRegistered && !canRegisterAgain && isScoringOpen")
    h3.registration-header {{ __('Registrant Information') }}
    .registration
      .division-detail(v-if="existingRegistrations[0]?.divisionAttributes.gender")
        span {{ __('Gender') }}
        strong {{ existingRegistrations[0]?.divisionAttributes.gender }}
      .division-detail(v-if="previousRegistrations.length" v-for="(prev, index) in previousRegistrations" :key="prev.externalRegistrationId")
        span(v-if="index === 0") {{ __(resultLabel) }}
        span(v-else)
        strong {{ formatPreviousResult(prev) }}
      .division-detail(v-if="activeDivision?.divisionLabel")
        span {{ __('Active Division') }}
        strong {{ activeDivision.divisionLabel }}
    ScoreSubmissionForm(
      v-if="open && activeRegistration"
      :competition="competition"
      :divisionId="divisionId"
      :registration-data="registrationData"
      :scores="scores[activeRegistration.externalRegistrationId]"
      :submit-handler="handleSubmit"
      @success="handleSuccess"
      @error="handleError"
    )
    ul.need-help(v-if="open")
      li Need help with your registration?&nbsp;
        a(href="mailto:challenges@roguefitness.com") Send us an email
      li Want to register for a friend?
        .link(@click="registerOther") Create a new Rogue Account
  .register-buttons(v-else)
    RhpaRegistration(:buyRequest="buyRequest")
  Drawer(v-if="showScoreConfirmation" @hide="rejectSubmit()" title="Score Submission Confirmation")
    .confirmation-body
      p {{ __('Before you submit your score, please verify that you have completed all of the following requirements:') }}
      .score-submission-checks(v-for="(check, index) in eventConfig.scoreSubmissionChecks" :key="check.value")
        Checkbox(
          :required="true"
          :modelValue="!!checkConfirmed[index]"
          @update:modelValue="checkConfirmed[index] = $event"
        )
        span.check-text(@click="checkConfirmed[index] = !checkConfirmed[index]")
          | {{ __(check.value) }}
    template(#footer)
      .footer-buttons
        RogueButton.confirm-cancel(width="full" color="gray" @click="rejectSubmit()" :disabled="isSubmitting") {{ __('Cancel') }}
        RogueButton.confirm-submit(width="full" color="red" :disabled="!canSubmitScores" @click="resolveSubmit()" :loading="isSubmitting") {{ __('Submit Scores') }}
</template>
<script setup lang="ts">
import { onMounted, ref, computed, watch, defineAsyncComponent  } from 'vue'
import type { PropType } from 'vue'
import { useRoute } from 'vue-router'
import { useCustomerStore } from '@/js/stores/customerStore'
import { useEventStore } from '@/js/stores/eventStore'
import { useUIStore } from '@/js/stores/uiStore'
import type { IEvent, IEventRegistration } from '@/js/stores/eventTypes.d.ts'
import { messages } from '@/js/app-bridge/messages'
import { useRogue } from '@/js/services/Rogue'
import Loading from '@/js/components/loading.vue'
import type { BuyRequest } from '@/js/services/RogueTypes'
import '@rogue/score-submission-kit/style.css'

const ScoreSubmissionForm = defineAsyncComponent(() => import('@rogue/score-submission-kit').then(mod => mod.ScoreSubmissionForm))
// Hold the spinner while the chunk downloads - without it the panel blanks between `loaded`
// flipping true and this component mounting
const RhpaRegistration = defineAsyncComponent({
  loader           : () => import('@/js/components/rhpa/rhpa-registration.vue'),
  loadingComponent : Loading,
  delay            : 0,
})

const props = defineProps({
  buyRequest   : { type: Object as PropType<BuyRequest>, required: true },
  sku          : { type: String, required: true },
  productTitle : { type: String, required: true },
})

const loaded = ref(false)

const customerStore = useCustomerStore()
const eventStore = useEventStore()
const uiStore = useUIStore()
const rogue = useRogue()
const route = useRoute()

const showScoreConfirmation = ref<boolean>(false)
const checkConfirmed = ref<{ [key: number]: boolean }>({})
// Events with multiple workouts call handleSubmit once per score, so only confirm once per registration
const confirmedRegistrationId = ref<string | null>(null)
const submissionCancelled = ref<boolean>(false)
const canSubmitScores = ref<boolean>(false)
const isSubmitting = ref<boolean>(false)

const isRobueBuilderRhpa = computed<boolean>(() => route.path.startsWith('/__rb__'))

watch(checkConfirmed, (newVal) => {
  const allChecked = Object.keys(checkConfirmed.value).length === eventConfig.value.scoreSubmissionChecks?.length && Object.values(newVal).length > 0 && Object.values(newVal).every(v => !!v)
  canSubmitScores.value = allChecked
}, { deep: true })

const overlay = computed(() => uiStore.overlay)

const loggedIn = computed<boolean>(() => customerStore.loggedIn)

const eventConfig = ref<Partial<IEvent>>({ open: '', close: '', season: '' })

const competition = computed(() => eventConfig.value.externalData)

const divisionId = computed(() => activeRegistration.value?.externalDivisionId)
const registrationData = computed(() => activeRegistration.value?.registrationResult)
const scores = ref<{ [registrationId: string]: any[] }>({})

const open = computed<boolean>(() => {
  const openDate = new Date(eventConfig.value.open || '')
  const closeDate = new Date(eventConfig.value.close || '')

  return openDate && openDate.getTime() < Date.now() && (!eventConfig.value.close || closeDate.getTime() > Date.now())
})

const existingRegistrations = computed<IEventRegistration[]>(() => {
  const loadDivisionAttributes = (divisionId: string) => {
    const division = eventStore.events[props.sku].divisions.find(d => d.id === divisionId)

    return division?.attributes
  }
  const registrations = eventStore.registrations
  const multiDivisions = registrations.filter(r => (r.divisionId || '').indexOf(',') > -1)

  const result = registrations.filter(r => r.eventSku === props.sku
    && r.eventSeason === eventConfig.value.season
    && ((r.divisionId || '').indexOf(',') !== -1 || !multiDivisions.find(mD => mD.orderId === r.orderId)))

  if (!result) {
    return []
  }

  result.forEach(r => r.divisionAttributes = loadDivisionAttributes(r.divisionId!))

  return result
})

const activeRegistration = computed<IEventRegistration | undefined>(() => existingRegistrations.value?.find(r => !scores.value[r.externalRegistrationId].length || !scores.value[r.externalRegistrationId]?.every(s => s.verified)))

const activeDivision = computed(() => {
  if (!activeRegistration.value) {
    return null
  }

  return eventConfig.value.divisions?.find(d => d.id === activeRegistration.value?.divisionId)
})

const previousRegistrations = computed<IEventRegistration[]>(() => existingRegistrations.value.filter(r => r.externalRegistrationId !== activeRegistration.value?.externalRegistrationId))

const alreadyRegistered = computed(() => !!existingRegistrations.value.length)

const isClosed = computed<boolean>(() => {
  if (!eventConfig.value.close) {
    return false
  }

  const close = new Date(eventConfig.value.close)

  return close.getTime() < Date.now()
})

const scoringOpenDate = computed<Date | null>(() => {
  if (!eventConfig.value?.externalData?.date_time_start) {
    return null
  }

  return new Date(eventConfig.value.externalData.date_time_start)
})

const isScoringOpen = computed<boolean>(() => {
  if (!scoringOpenDate.value) {
    return false
  }

  return scoringOpenDate.value.getTime() <= Date.now()
})

const allScoresVerified = computed<boolean>(() => Object.keys(scores.value).length > 0 && Object.values(scores.value).every((value: any[]) => value.some(s => s.verified === true)))

// If you have already registered once before and there are other divisions that you are still elligible for.
const canRegisterAgain = computed<boolean>(() => {
  return !!existingRegistrations.value?.length
    && !!eventConfig.value.allowMultipleRegistrations
    && allScoresVerified.value
    && !!eventConfig.value.divisions?.filter(d => !d.attributes?.gender || d.attributes?.gender === existingRegistrations.value[0].divisionAttributes?.gender)
      .filter(d => !existingRegistrations.value.map(r => r.divisionId).includes(d.id))
      .length
})

const resultLabel = computed<string>(() => {
  if (previousRegistrations.value.length > 1) {
    return canRegisterAgain.value ? 'Previous Results' : 'Results'
  } else {
    return canRegisterAgain.value ? 'Previous Result' : 'Result'
  }
})

async function updateScores(registrationId: string) {
  try {
    scores.value[registrationId] = await rogue.api.fetch<any[]>({ url: `/svc_event/account/registrations/${registrationId}/scores` }) || []
  } catch (error) {
    rogue.log.error('Error retrieving scores for customer', error)
  }
}

// load() awaits customerStore.init(), which is what flips loggedIn on first load. That wakes the
// loggedIn watcher below, which would redo the same work and bounce `loaded` back to false -
// flickering the spinner off and on again mid-load.
let loadInFlight = false

async function load() {
  loadInFlight = true
  loaded.value = false

  try {
    // Skip customer init in RogueBuilder preview — no authenticated session available
    if (!isRobueBuilderRhpa.value) {
      await customerStore.init(rogue)
    }

    await eventStore.loadEventConfig(props.sku, rogue)

    eventConfig.value = eventStore.events[props.sku] || { open: '', close: '' }

    if (loggedIn.value && !isRobueBuilderRhpa.value) {
      await eventStore.loadProfile(rogue)
    }

    if (alreadyRegistered.value && !isRobueBuilderRhpa.value) {
      await Promise.all(existingRegistrations.value.map(r => updateScores(r.externalRegistrationId)))
    }

    loaded.value = true
  } finally {
    loadInFlight = false
  }
}

function format(value: string) {
  const label = value.charAt(0).toUpperCase() + value.substring(1)

  return label.replace(/([A-Z])/g, ' $1').trim()
}

function formatPreviousResult(prev: IEventRegistration): string {
  const scoreData = scores.value[prev.externalRegistrationId]?.[0]
  
  return `${scoreData?.raw_value}${(scoreData?.score_measure_units || '').toUpperCase()} (${eventConfig.value.divisions?.find(d => d.id === prev.divisionId)?.divisionLabel})`
}

function closeSubmissionDrawer() {
  showScoreConfirmation.value = false
  checkConfirmed.value = {}
}

// The form submits each workout back-to-back, so cancelling drops the rest of that batch without
// reopening the drawer. The timeout lands after the batch drains, leaving a later submission free to confirm again.
let cancelResetTimer: ReturnType<typeof setTimeout>

function cancelSubmissions() {
  submissionCancelled.value = true
  clearTimeout(cancelResetTimer)
  cancelResetTimer = setTimeout(() => submissionCancelled.value = false)
}

async function submitScores(payload: any) {
  const response = await rogue.api.fetch({
    url    : `/svc_event/account/registrations/${activeRegistration.value?.externalRegistrationId}/scores`,
    method : 'POST',
    data   : JSON.parse(JSON.stringify(payload)),
  })

  return response
}

let resolveSubmit: (value: any) => void
let rejectSubmit: (reason?: any) => void

async function handleSubmit(payload: any) {
  if (submissionCancelled.value) {
    throw new Error('Score submission was cancelled')
  }

  const registrationId = activeRegistration.value?.externalRegistrationId || null
  const isCompetitive = !!eventConfig.value.externalData?.divisions?.find(d => d.id === activeRegistration.value?.externalDivisionId)?.is_competitive
  const needsConfirmation = confirmedRegistrationId.value !== registrationId

  if (eventConfig.value.scoreSubmissionChecks?.length && isCompetitive && needsConfirmation) {
    showScoreConfirmation.value = true

    return new Promise((resolve, reject) => {
      resolveSubmit = async () => {
        try {
          isSubmitting.value = true
          const response = await submitScores(payload)
          isSubmitting.value = false
          confirmedRegistrationId.value = registrationId
          closeSubmissionDrawer()
          resolve(response)
        } catch (error) {
          isSubmitting.value = false
          reject(error)
        }
      }

      rejectSubmit = () => {
        cancelSubmissions()
        closeSubmissionDrawer()
        reject(new Error('Score submission was cancelled'))
      }
    })
  } else {
    try {
      const response = await submitScores(payload)

      return response
    } catch (error) {
      // A failed submission re-requires the checks, so the next attempt goes back through the drawer
      confirmedRegistrationId.value = null

      throw error
    }
  }
}

// Withdraw functionality is not currently used
// async function handleWithdraw(payload: any) {
//   const response = await rogue.api.fetch({
//     url    : `/svc_event/account/registrations/${registrationId.value}/scores/${payload.scoreId}`,
//     method : 'DELETE',
//     data   : JSON.parse(JSON.stringify(payload)),
//   })
  
//   return response
// }

function handleSuccess(result: any) {
  rogue.log.info('Event Score submitted!', result)

  updateScores(activeRegistration.value!.externalRegistrationId)
}

function handleError(error: any) {
  rogue.log.error('Event Score Submission error:', error)
}

function registerOther() {
  customerStore.logout(rogue)
  if (rogue.appContext) {
    rogue.emitToApp(messages.LOGOUT, { redirect: route.fullPath })
  }
}

watch(() => props.sku, () => {
  load()
})

// Picks up a login that happens after the initial load, e.g. through the inline login form. A load
// already in flight covers this work itself, so stay out of its way.
watch(loggedIn, async (isLoggedIn, wasLoggedIn) => {
  if (loadInFlight || !isLoggedIn || wasLoggedIn || isRobueBuilderRhpa.value) {
    return
  }

  loaded.value = false
  await eventStore.loadProfile(rogue)

  if (alreadyRegistered.value) {
    await Promise.all(existingRegistrations.value.map(r => updateScores(r.externalRegistrationId)))
  }
  loaded.value = true
})

onMounted(() => {
  load()
})
</script>
<style lang="scss" scoped>
.already-registered {
  margin-bottom: $space-s;
}

.registration-header {
  font-size: $size-xxs;
  margin-bottom: $space-xxs;
}

.registration {
  margin-bottom: $space-s;
  display: flex;
  flex-direction: column;
  gap: $space-xs;
}

.need-help {
  margin: $space-s 0;
  font-size: $size-xs;
}

.division-detail {
  display: flex;
  justify-content: space-between;
  font-size: $size-xs;
}

.confirmation-body {
  padding: $space-s;
}

.score-submission-checks {
  display: flex;
  flex-direction: row;
  align-items: center;
  cursor: pointer;
  margin-bottom: $space-xs;
}

.confirm-actions {
  display: flex;
  margin-top: $space-m;
  justify-content: flex-end;
  gap: $space-xs;
}

.footer-buttons {
  box-shadow: 0px -2px 8px 0px $color-gray-light;
  display: flex;
  gap: $space-s;
  padding: $space-s;
}
</style>
